import React, { useState, useRef, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { X, Send, Loader2, Sparkles, GripVertical } from 'lucide-react';

// Per-page greeting hints so Zippie opens with a helpful first message.
const GREETING_MAP = [
  ['/pricing', "Want help picking the right Zazu plan? Tell me how many kids and their ages, and I'll recommend one."],
  ['/billing', "Need help with your subscription or payment history? I've got you."],
  ['/careers', "Curious about roles at Zazu? I can walk you through the application or intern program."],
  ['/employee/hr-profile', "Need help finishing your HR profile? It only takes a minute — I'll walk you through it."],
  ['/employee/resources', "Looking for a template, doc, or brand asset? I can help you find it in Resources."],
  ['/employee/team', "Want to find a teammate or see who reports to whom? Ask away!"],
  ['/social-hub', "Ready to draft a post? Tell me the platform and topic — I'll help you write it."],
  ['/intern/dashboard', "I'm here to help with your daily tasks. Which one should we tackle first?"],
  ['/admin/careers', "Managing candidates today? I can help with pipelines, offers, or job posts."],
  ['/admin', "Admin console — ask me about users, licenses, analytics, or announcements."],
  ['/christian', "Want to explore Bible lessons or worship content by age? Tell me the age group."],
  ['/dashboard', "Welcome back! Want help picking today's activity or checking your kid's progress?"],
];

const greetingFor = (path, name) => {
  const p = (path || '').toLowerCase().replace(/\/+$/, '') || '/';
  const match = GREETING_MAP
    .filter(([prefix]) => p === prefix || p.startsWith(prefix + '/') || (prefix !== '/' && p.startsWith(prefix)))
    .sort((a, b) => b[0].length - a[0].length)[0];
  const first = name ? name.split(' ')[0] : 'there';
  if (match) return `Hi ${first}! ${match[1]}`;
  return `Hi ${first}! I'm Zippie 🐧 — how can I help you today?`;
};

const POS_KEY = 'zazu-zippie-pos';
const DEFAULT_POS = { x: null, y: null }; // null → use CSS default (bottom-right)

const ZippieAssistant = () => {
  const { api, user } = useAuth();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [pos, setPos] = useState(() => {
    try { return JSON.parse(localStorage.getItem(POS_KEY)) || DEFAULT_POS; }
    catch { return DEFAULT_POS; }
  });
  const [dragging, setDragging] = useState(false);
  const dragState = useRef({ startX: 0, startY: 0, origX: 0, origY: 0, moved: false });
  const sessionRef = useRef(null);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (open && messages.length === 0) {
      setMessages([{ role: 'assistant', content: greetingFor(location.pathname, user?.name) }]);
    }
  }, [open, messages.length, user, location.pathname]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, sending]);

  // ---- Drag logic (pointer events — works for mouse and touch) ----
  const onPointerDown = (e) => {
    if (open) return; // only drag when closed (button)
    const btn = e.currentTarget.getBoundingClientRect();
    dragState.current = {
      startX: e.clientX,
      startY: e.clientY,
      origX: btn.left,
      origY: btn.top,
      moved: false,
    };
    setDragging(true);
    e.currentTarget.setPointerCapture(e.pointerId);
  };
  const onPointerMove = (e) => {
    if (!dragging) return;
    const dx = e.clientX - dragState.current.startX;
    const dy = e.clientY - dragState.current.startY;
    if (Math.abs(dx) > 4 || Math.abs(dy) > 4) dragState.current.moved = true;
    const nx = dragState.current.origX + dx;
    const ny = dragState.current.origY + dy;
    // Clamp inside viewport with 8px margin
    const w = 56, h = 56;
    const maxX = window.innerWidth - w - 8;
    const maxY = window.innerHeight - h - 8;
    setPos({ x: Math.max(8, Math.min(nx, maxX)), y: Math.max(8, Math.min(ny, maxY)) });
  };
  const onPointerUp = (e) => {
    if (!dragging) return;
    setDragging(false);
    try { e.currentTarget.releasePointerCapture(e.pointerId); } catch { /* ignore */ }
    // If it didn't move, treat as click
    if (!dragState.current.moved) {
      setOpen(true);
    } else {
      // Snap to nearest side for polish
      const snapped = { ...pos };
      if (snapped.x !== null) {
        const midX = window.innerWidth / 2;
        snapped.x = pos.x + 28 < midX ? 8 : window.innerWidth - 56 - 8;
      }
      setPos(snapped);
      try { localStorage.setItem(POS_KEY, JSON.stringify(snapped)); } catch { /* ignore */ }
    }
  };

  const send = async (e) => {
    e?.preventDefault?.();
    const text = input.trim();
    if (!text || sending) return;
    setInput('');
    setMessages((m) => [...m, { role: 'user', content: text }]);
    setSending(true);
    try {
      const res = await api.post('/zippie/chat', {
        message: text,
        session_id: sessionRef.current,
        page_path: location.pathname,
      });
      if (!sessionRef.current) sessionRef.current = res.data?.session_id;
      setMessages((m) => [...m, { role: 'assistant', content: res.data?.reply || '…' }]);
    } catch (err) {
      setMessages((m) => [...m, { role: 'assistant', content: 'Sorry — I hit a snag. Try again in a moment.' }]);
    } finally {
      setSending(false);
    }
  };

  if (!user) return null;

  return (
    <>
      {/* Floating draggable mascot button */}
      {!open && (
        <button
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onPointerCancel={onPointerUp}
          aria-label="Open Zippie assistant (drag to move)"
          data-testid="zippie-open-btn"
          className="fixed z-40 group touch-none select-none"
          style={pos.x !== null ? { left: pos.x, top: pos.y } : { bottom: 24, left: 24 }}
        >
          <div className={`w-14 h-14 rounded-full bg-gradient-to-br from-teal-500 to-teal-700 shadow-lg hover:shadow-xl flex items-center justify-center overflow-hidden border-4 border-white transition-transform ${dragging ? 'scale-110 cursor-grabbing' : 'group-hover:scale-105 cursor-grab'}`}>
            <img src="/zippy/zippy-head.png" alt="Zippie" className="w-full h-full object-cover pointer-events-none" draggable={false} />
          </div>
          <span className="absolute -top-2 -right-1 bg-emerald-500 text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full shadow">AI</span>
          <GripVertical className="absolute -left-1 top-1/2 -translate-y-1/2 w-3 h-3 text-slate-400 opacity-0 group-hover:opacity-100 transition-opacity" aria-hidden />
        </button>
      )}

      {/* Chat panel */}
      {open && (
        <div
          className="fixed z-50 bottom-6 right-6 w-[min(380px,calc(100vw-2rem))] h-[min(560px,calc(100vh-4rem))] bg-white rounded-2xl shadow-2xl border border-slate-200 flex flex-col overflow-hidden animate-in fade-in slide-in-from-bottom-4 duration-200"
          data-testid="zippie-panel"
        >
          <div className="p-3 bg-gradient-to-r from-teal-600 to-teal-700 text-white flex items-center gap-2">
            <img src="/zippy/zippy-head.png" alt="Zippie" className="w-9 h-9 rounded-full bg-white/20 object-cover" />
            <div className="flex-1 min-w-0">
              <h3 className="font-semibold text-sm flex items-center gap-1.5">Zippie <Sparkles className="w-3.5 h-3.5 text-amber-300" /></h3>
              <p className="text-xs text-teal-100">Your Zazu AI assistant</p>
            </div>
            <button
              onClick={() => setOpen(false)}
              className="p-1.5 rounded-full hover:bg-white/20 transition-colors"
              aria-label="Close"
              data-testid="zippie-close-btn"
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          <div ref={scrollRef} className="flex-1 overflow-y-auto p-3 space-y-3 bg-slate-50" data-testid="zippie-messages">
            {messages.map((m, i) => (
              <div key={i} className={`flex gap-2 ${m.role === 'user' ? 'flex-row-reverse' : ''}`}>
                {m.role === 'assistant' && (
                  <img src="/zippy/zippy-head.png" alt="" className="w-7 h-7 rounded-full flex-shrink-0" />
                )}
                <div
                  className={`px-3 py-2 rounded-2xl text-sm max-w-[80%] whitespace-pre-wrap ${
                    m.role === 'user'
                      ? 'bg-teal-600 text-white rounded-br-sm'
                      : 'bg-white border border-slate-200 text-slate-700 rounded-bl-sm'
                  }`}
                  data-testid={`zippie-msg-${m.role}`}
                >
                  {m.content}
                </div>
              </div>
            ))}
            {sending && (
              <div className="flex gap-2 items-center text-slate-500 text-xs">
                <img src="/zippy/zippy-head.png" alt="" className="w-7 h-7 rounded-full" />
                <Loader2 className="w-4 h-4 animate-spin" /> Zippie is thinking…
              </div>
            )}
          </div>

          <form onSubmit={send} className="p-2 border-t border-slate-200 bg-white flex items-center gap-2">
            <input
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Ask Zippie anything…"
              className="flex-1 px-3 py-2 border border-slate-200 rounded-full text-sm focus:outline-none focus:border-teal-400"
              disabled={sending}
              data-testid="zippie-input"
            />
            <Button
              type="submit"
              size="sm"
              disabled={sending || !input.trim()}
              className="rounded-full bg-teal-600 hover:bg-teal-700 w-9 h-9 p-0"
              data-testid="zippie-send-btn"
            >
              <Send className="w-4 h-4" />
            </Button>
          </form>
        </div>
      )}
    </>
  );
};

export default ZippieAssistant;
